import {
  LLMFeatureEnablementState,
  LLMDataAccessConsentState,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as Net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export interface LLMFeatureEnablementUpdate {
  configKey: string;
  enablementState: LLMFeatureEnablementState;
  explicitlyEnabledUserIdsToAdd: Array<string>;
  explicitlyEnabledUserIdsToRemove: Array<string>;
}

export interface NotebookCheckConfigUpdate {
  /**
   * Note that "null" means "set guidelines to null" (meaning they switch to the default guidelines).
   *
   * To avoid setting the guidelines, notebookCheckConfigUpdate should be null.
   */
  guidelines: string | null;
}

export interface AssistantConfigUpdate {
  /**
   * To avoid setting the guidelines, assistantConfigUpdate should be null.
   */
  guidelines: string | null;
}

export interface LLMFeaturesConfigUpdate {
  isOpenAIEnabledUpdate: boolean | null;
  isGoogleCloudEnabledUpdate: boolean | null;
  dataAccessConsentUpdate: LLMDataAccessConsentState | null;
  featureEnablementUpdates: Array<LLMFeatureEnablementUpdate>;
  notebookCheckConfigUpdate: NotebookCheckConfigUpdate | null;
  assistantConfigUpdate: AssistantConfigUpdate | null;
}

export function updateLLMFeaturesConfig(llmFeaturesConfigUpdate: LLMFeaturesConfigUpdate): Promise<void> {
  return Net.postJSONPromise({
    url: '/1/api/tenant:update-llm-features-config',
    data: llmFeaturesConfigUpdate,
  });
}

export interface UsageExportOpts {
  resource?: string;
  from_timestamp?: Date;
  to_timestamp?: Date;
  product?: string;
  user_id?: string;
  // false drops free (non-credit) usage, matching the chart's "include" checkbox.
  include_non_credit_products?: boolean;
}

export interface UsageExportResult {
  url: string;
  // truncated = backend hit the time limit, so the CSV is partial; `message` says so for the admin.
  truncated?: boolean;
  message?: string;
}

/**
 * Kick off the AI usage export. The CSV is built in an async task (slow for wide ranges), so poll it
 * via {@link TaskRunner} and resolve with the signed download URL.
 */
export function buildUsageExportRequestData(opts: UsageExportOpts = {}): Record<string, unknown> {
  const data: Record<string, unknown> = {};

  if (opts.resource) {
    data.resource = opts.resource;
  }
  if (opts.from_timestamp) {
    data.fromTimestamp = opts.from_timestamp.toISOString();
  }
  if (opts.to_timestamp) {
    data.toTimestamp = opts.to_timestamp.toISOString();
  }
  if (opts.product) {
    data.product = opts.product;
  }
  if (opts.user_id) {
    data.userId = opts.user_id;
  }
  if (opts.include_non_credit_products !== undefined) {
    data.includeNonCreditProducts = opts.include_non_credit_products;
  }

  return data;
}

export function exportUsageReport(opts: UsageExportOpts = {}): Promise<UsageExportResult> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/metering/usage:export',
    data: buildUsageExportRequestData(opts),
  });
}

export interface EntitlementValueResponse {
  hasEntitlement: boolean;
  balance?: number;
  usage?: number;
  overage?: number;
  grantedAmount?: number;
}

export function queryEntitlementValue(resourceType: string = 'AI_CREDIT'): Promise<EntitlementValueResponse> {
  return Net.getJSONPromise(`/1/api/metering/entitlement?resource_type=${encodeURIComponent(resourceType)}`);
}
